import InfiniteScrollDelegate from './infiniteScroll.delegate';

/**
 * Infinite scroll: bottom sentinel + IntersectionObserver.
 * Used as `class InfiniteScroll extends Inherit(InfiniteScrollController, Component)`.
 */
class InfiniteScrollController extends InfiniteScrollDelegate {
    componentDidMount() {
        this._installReadyDelay();
        this._installObserver();
    }

    componentDidUpdate(prevProps, prevState) {
        if (
            prevProps.initialDelayMs !== this.props.initialDelayMs ||
            prevProps.resetKey !== this.props.resetKey
        ) {
            this._installReadyDelay();
        }

        const rm = this.props.rootMargin;
        const prevRm = prevProps.rootMargin;

        const observerDepsChanged =
            prevState.ready !== this.state.ready ||
            prevProps.hasMore !== this.props.hasMore ||
            prevProps.disabled !== this.props.disabled ||
            prevProps.scrollRoot !== this.props.scrollRoot ||
            prevRm !== rm ||
            prevProps.resetKey !== this.props.resetKey;

        if (observerDepsChanged) {
            this._installObserver();
        }
    }

    componentWillUnmount() {
        this._teardownObserver();
        if (this._readyTimer) {
            window.clearTimeout(this._readyTimer);
            this._readyTimer = null;
        }
    }

    _installReadyDelay() {
        const { initialDelayMs } = this.props;

        if (this._readyTimer) {
            window.clearTimeout(this._readyTimer);
            this._readyTimer = null;
        }

        if (initialDelayMs <= 0) {
            if (!this.state.ready) {
                this.setReady(true);
            }
            return;
        }

        this._teardownObserver();
        this.setReady(false, () => {
            this._readyTimer = window.setTimeout(() => {
                this._readyTimer = null;
                this.setReady(true);
            }, initialDelayMs);
        });
    }

    _runEndReached() {
        const { onEndReached, loading } = this.props;
        if (loading || this._inFlight || !onEndReached) return;

        this._inFlight = true;
        Promise.resolve(onEndReached())
            .catch(() => {})
            .finally(() => {
                this._inFlight = false;
            });
    }

    _installObserver() {
        this._teardownObserver();

        const { hasMore, disabled, scrollRoot, rootMargin } = this.props;
        const margin = rootMargin;

        if (!this.state.ready || disabled || !hasMore) {
            return;
        }

        const sentinel = this.sentinelRef?.current;
        if (!sentinel) {
            return;
        }

        const observer = new IntersectionObserver(
            (entries) => {
                if (!entries.some((e) => e.isIntersecting)) return;
                this._runEndReached();
            },
            { root: scrollRoot || null, rootMargin: margin, threshold: 0 }
        );

        observer.observe(sentinel);
        this._observer = observer;
    }

    _teardownObserver() {
        if (this._observer) {
            this._observer.disconnect();
            this._observer = null;
        }
    }
}

export default InfiniteScrollController;
